import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart, Trash2, Package, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useWishlist } from '../context/WishlistContext';
import { useQuoteCart } from '../context/QuoteCartContext';
import { Helmet } from 'react-helmet-async';

export const Favoritos = () => {
  const { wishlist, removeFromWishlist, clearWishlist, wishlistCount } = useWishlist();
  const { addProduct } = useQuoteCart();

  const handleAddToCart = (item) => {
    // Converter item do wishlist para formato do produto
    const product = {
      id: item.id,
      name: item.name,
      brand: item.brand,
      images: item.image ? [item.image] : [],
      size: item.size,
      slug: item.slug
    };
    addProduct(product);
  };

  return (
    <>
      <Helmet>
        <title>Meus Favoritos - Disneylândia Cosméticos</title>
        <meta name="description" content="Seus produtos favoritos salvos para consulta rápida" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-pink-50/30 to-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[#EA1266] to-[#d00f58] rounded-2xl flex items-center justify-center shadow-lg shadow-pink-500/20">
                  <Heart size={24} className="text-white fill-white" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A]">
                    Meus Favoritos
                  </h1>
                  <p className="text-gray-500 mt-1">
                    {wishlistCount} {wishlistCount === 1 ? 'produto salvo' : 'produtos salvos'}
                  </p>
                </div>
              </div>

              {wishlistCount > 0 && (
                <Button
                  variant="outline"
                  onClick={clearWishlist}
                  className="text-red-500 border-red-200 hover:bg-red-50 hover:border-red-300 rounded-full"
                >
                  <Trash2 size={16} className="mr-2" />
                  Limpar Tudo
                </Button>
              )}
            </div>

            <p className="text-gray-600 max-w-2xl">
              Seus produtos favoritos ficam salvos aqui para você consultar quando quiser. 
              Não perca seus produtos especiais! ❤️
            </p>
          </div>

          {/* Empty State */}
          {wishlistCount === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="w-32 h-32 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full flex items-center justify-center mb-6 relative">
                <Heart size={64} className="text-[#EA1266]" />
                <Sparkles size={24} className="text-[#EA1266] absolute top-2 right-4 animate-pulse" />
              </div>
              <h2 className="text-2xl font-bold text-[#0A0A0A] mb-3">
                Nenhum favorito ainda
              </h2>
              <p className="text-gray-500 text-center max-w-md mb-8">
                Comece a adicionar produtos aos favoritos clicando no ❤️ em qualquer produto que você goste!
              </p>
              <Link to="/catalogo">
                <Button className="bg-[#EA1266] hover:bg-[#d00f58] text-white px-8 py-6 rounded-full text-base">
                  <Package size={20} className="mr-2" />
                  Explorar Produtos
                </Button>
              </Link>
            </div>
          ) : (
            /* Lista de Favoritos */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {wishlist.map((item) => (
                <Card
                  key={item.id}
                  className="group hover:shadow-xl transition-all duration-300 overflow-hidden rounded-2xl border-gray-100"
                >
                  <div className="relative aspect-square bg-[#F3F4F6]">
                    {item.image ? (
                      <Link to={`/produto/${item.slug}`}>
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.src = 'https://placehold.co/400x400?text=Produto';
                          }}
                        />
                      </Link>
                    ) : (
                      <Link to={`/produto/${item.slug}`} className="w-full h-full flex items-center justify-center">
                        <Package size={48} className="text-gray-300" />
                      </Link>
                    )}
                    
                    {/* Botão Remover */}
                    <button
                      onClick={() => removeFromWishlist(item.id)}
                      className="absolute top-3 right-3 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-red-50 transition-all active:scale-95"
                      aria-label="Remover dos favoritos"
                    >
                      <Heart size={20} className="text-red-500 fill-red-500" />
                    </button>
                  </div>

                  <CardContent className="p-4">
                    <span className="text-xs font-semibold text-[#0A0A0A] bg-black/5 px-2.5 py-1 rounded-full inline-block mb-2">
                      {item.brand}
                    </span>
                    <Link to={`/produto/${item.slug}`}>
                      <h3 className="font-semibold text-[#0A0A0A] mb-1 line-clamp-2 hover:text-[#EA1266] transition-colors min-h-[3rem]">
                        {item.name}
                      </h3>
                    </Link>
                    {item.size && (
                      <p className="text-xs text-gray-500 mb-3">{item.size}</p>
                    )}

                    <div className="flex gap-2 mt-4">
                      <Button
                        onClick={() => handleAddToCart(item)}
                        className="flex-1 bg-[#EA1266] hover:bg-[#d00f58] text-white rounded-full text-sm h-10"
                      >
                        <ShoppingCart size={16} className="mr-1.5" />
                        Orçamento
                      </Button>
                      <Link to={`/produto/${item.slug}`} className="flex-1">
                        <Button
                          variant="outline"
                          className="w-full border-[#EA1266] text-[#EA1266] hover:bg-pink-50 rounded-full text-sm h-10"
                        >
                          Ver Mais
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* CTA Bottom */}
          {wishlistCount > 0 && (
            <div className="mt-12 bg-gradient-to-r from-pink-50 to-purple-50 rounded-3xl p-8 text-center">
              <h3 className="text-2xl font-bold text-[#0A0A0A] mb-3">
                Gostou dos seus favoritos?
              </h3>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Adicione todos ao orçamento e receba um atendimento personalizado via WhatsApp!
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Link to="/catalogo">
                  <Button variant="outline" className="rounded-full px-8 border-2 border-[#EA1266] text-[#EA1266] hover:bg-pink-50">
                    Continuar Explorando
                  </Button>
                </Link>
                <Link to="/orcamento">
                  <Button className="bg-[#64BC46] hover:bg-[#56a33c] text-white rounded-full px-8">
                    Ir ao Orçamento
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
